import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useWorkspaceStore } from '../../stores/workspaceStore'
import { Alert, Card, CardHeader, Field, FieldGrid, FieldLabel, Hint, SaveBar, Stack, TileGrid } from '../../ds'
import { mergeSmsConfig, patchSmsConfig, SMS_CONFIG_DEFAULTS, type SmsConfig } from './smsConfigWriter'
import { matchesStored } from './storedValue'
import { quietWindowProblem, SMS_QUIET_HOURS_FLOOR } from '@marva/shared/quiet-window'

/**
 * SMS limits — the caps half of `smsConfig`, on the Limits tab (App IA v5 · phase 2c).
 *
 * Limits is "the compliance floor plus commercial limits — the merchant's real control"
 * (`settings-consolidation.md` §3.6), so the ceilings live here beside discount governance and
 * the engagement rules. Whether SMS is switched on at all, and the Twilio credentials behind
 * it, are connection state and stay on Channels.
 *
 * ⚠️ Every write goes through `patchSmsConfig`. The PUT replaces `smsConfig` whole, so a
 * second writer sending its own half would revert this one — see `smsConfigWriter.ts`.
 *
 * ⚠️ **Every piece of local state here is scoped at the READ, and there is no reseeding
 * effect.** An effect runs after paint, so React renders once with the new workspace id and
 * the old caps before it fires — and a save landing in that window reads the previous
 * workspace's numbers while `patchSmsConfig` resolves the target as the new one, writing A's
 * caps onto B. The writer's mid-flight guard cannot catch that: the workspace genuinely is B.
 * This is the PR #103 lesson (*scope cached state at the read*) and `AssistantNameCard` and
 * `AuthorityPanel` hold the same shape.
 */

type Caps = Pick<
  SmsConfig,
  'dailyLimitMessages' | 'monthlyLimitMessages' | 'maxSmsPerCustomerMonth' | 'quietHoursStart' | 'quietHoursEnd'
>

/**
 * The form's own shape, where the three numeric caps may be momentarily EMPTY.
 *
 * ⚠️ Not a nicety. A number input whose `onChange` refuses a non-numeric value cannot be
 * cleared at all: select-all-and-retype leaves the old digits in place and the new ones land
 * beside them, so retyping 12 over 40 stores 4012. The field has to be allowed to be empty
 * while it is being retyped, and `handleSave` is what refuses to send an empty one.
 */
type FormCaps = Omit<Caps, 'dailyLimitMessages' | 'monthlyLimitMessages' | 'maxSmsPerCustomerMonth'> & {
  dailyLimitMessages: number | null
  monthlyLimitMessages: number | null
  maxSmsPerCustomerMonth: number | null
}

/** Edits in progress, stamped with the workspace they belong to. */
interface Draft {
  workspaceId: string
  caps: FormCaps
}

/** The outcome of a save, stamped with the workspace it is news about. */
interface Outcome {
  workspaceId: string
  ok: boolean
  text: string
}

/**
 * The three numeric caps, their labels and their advertised range — in ONE place.
 *
 * The input's `min`/`max` attributes and the range `handleSave` refuses are read from the same
 * entry, so the card cannot advertise a ceiling it then accepts a value above. (It did: `whole`
 * checked "integer ≥ 1" and nothing else, so a merchant typing 500 into the per-customer field
 * stored 500 beside a line calling six the carrier maximum.)
 *
 * `PUT /workspaces` now validates `smsConfig` against `SmsConfigSchema` (MAR-163), which holds
 * the floor of these bounds (positive integers) and the quiet-hours rule — not these ceilings.
 * The advertised maxima are still this table's alone.
 */
const CAP_BOUNDS = {
  dailyLimitMessages: { label: 'Text messages a day', min: 1, max: 10000 },
  monthlyLimitMessages: { label: 'Text messages a month', min: 1, max: 100000 },
  maxSmsPerCustomerMonth: { label: 'Text messages to one customer a month', min: 1, max: 30 }
} as const

type CapKey = keyof typeof CAP_BOUNDS

const CAP_KEYS = Object.keys(CAP_BOUNDS) as CapKey[]

/** A whole number inside the range the card advertises for that field. */
const inRange = (value: number | null, key: CapKey): value is number =>
  Number.isInteger(value) && (value as number) >= CAP_BOUNDS[key].min && (value as number) <= CAP_BOUNDS[key].max

const capsOf = (config: SmsConfig | undefined): FormCaps => ({
  dailyLimitMessages: config?.dailyLimitMessages ?? SMS_CONFIG_DEFAULTS.dailyLimitMessages,
  monthlyLimitMessages: config?.monthlyLimitMessages ?? SMS_CONFIG_DEFAULTS.monthlyLimitMessages,
  maxSmsPerCustomerMonth: config?.maxSmsPerCustomerMonth ?? SMS_CONFIG_DEFAULTS.maxSmsPerCustomerMonth,
  quietHoursStart: config?.quietHoursStart ?? SMS_CONFIG_DEFAULTS.quietHoursStart,
  quietHoursEnd: config?.quietHoursEnd ?? SMS_CONFIG_DEFAULTS.quietHoursEnd
})

const hourLabel = (h: number) =>
  h === 0 ? '12:00 AM' : h < 12 ? `${h}:00 AM` : h === 12 ? '12:00 PM' : `${h - 12}:00 PM`

const HOURS = Array.from({ length: 24 }, (_, i) => i)

/** THE rule (MAR-163) for the window on this card: its own hours unioned with the 9pm–8am floor. */
const quietProblemOf = (caps: Pick<FormCaps, 'quietHoursStart' | 'quietHoursEnd'>) =>
  quietWindowProblem({ start: caps.quietHoursStart, end: caps.quietHoursEnd }, { floor: SMS_QUIET_HOURS_FLOOR })

/**
 * A whole-number cap. Empty is a transient editing state, never a saved one — see `FormCaps`.
 *
 * The label and the bounds come from `CAP_BOUNDS[capKey]` rather than from props, so the number
 * the field advertises and the number `handleSave` enforces are provably the same one.
 */
const CapField = ({
  capKey,
  detail,
  value,
  onChange
}: {
  capKey: CapKey
  detail: string
  value: number | null
  onChange: (v: number | null) => void
}) => {
  const { label, min, max } = CAP_BOUNDS[capKey]
  return (
    <Field
      type="number"
      label={label}
      hint={detail}
      min={min}
      max={max}
      value={value ?? ''}
      onChange={(e) => {
        const raw = (e.target as HTMLInputElement).value
        onChange(raw === '' ? null : parseInt(raw, 10))
      }}
    />
  )
}

const SmsLimitsSection = () => {
  const currentWorkspace = useWorkspaceStore((s) => s.currentWorkspace)
  const workspaceId = currentWorkspace?.id

  const [draft, setDraft] = useState<Draft | null>(null)
  const [outcome, setOutcome] = useState<Outcome | null>(null)
  const [saving, setSaving] = useState<string | null>(null)

  // ── Scoped at the read. Anything belonging to another workspace is simply not current. ──
  const stored = capsOf(currentWorkspace?.smsConfig)
  const caps = draft && draft.workspaceId === workspaceId ? draft.caps : stored
  const shown = outcome && outcome.workspaceId === workspaceId ? outcome : null
  const isSaving = saving === workspaceId

  const set = <K extends keyof FormCaps>(key: K, value: FormCaps[K]) => {
    if (!workspaceId) return
    setDraft({ workspaceId, caps: { ...caps, [key]: value } })
    setOutcome(null)
  }

  const handleSave = async () => {
    if (!workspaceId) return
    const asked = workspaceId
    // An empty, fractional or out-of-range field is refused here rather than sent. The PUT
    // replaces `smsConfig` whole (so a `null` cap would BECOME the stored cap) and the service
    // validates none of it (see `CAP_BOUNDS`), so this is the only place the advertised maximum
    // is real. Named per field, because "limits must be whole numbers" leaves a merchant who
    // typed 500 into one of three fields guessing which one the screen dislikes.
    const failing = CAP_KEYS.find((key) => !inRange(caps[key], key))
    if (failing) {
      const { label, min, max } = CAP_BOUNDS[failing]
      setOutcome({
        workspaceId: asked,
        ok: false,
        text: `“${label}” must be a whole number between ${min} and ${max}.`
      })
      return
    }
    // MAR-163: hours that, with the 9pm–8am floor, never open mean "never text again". Refused
    // here by the same rule `SmsConfigSchema` refuses the PUT with, so the card and the server
    // cannot disagree about which windows are savable.
    const quiet = quietProblemOf(caps)
    if (quiet) {
      setOutcome({ workspaceId: asked, ok: false, text: quiet.message })
      return
    }
    // ⚠️ The handler closure was built during a render of `asked`, but `patchSmsConfig`
    // resolves its target from the store at CALL time. If those have diverged, this click is
    // about a workspace the merchant has left, and sending it would write these caps onto
    // whatever they are looking at now.
    if (useWorkspaceStore.getState().currentWorkspace?.id !== asked) return
    setSaving(asked)
    setOutcome(null)
    // Only the caps. `enabled`, the Twilio credentials and `enabledCampaignTypes` belong to
    // another surface and are preserved by the merge — this screen must not name them.
    //
    // `failing` proved all three numeric caps are integers in range; TypeScript cannot carry
    // that through a `find`, so the narrowing is asserted once, here, rather than by running
    // the same three guards a second time and leaving two copies of the rule.
    const sent: Caps = caps as Caps
    try {
      const res = await patchSmsConfig(sent)
      if (!res.success) {
        // The typed value stays in the fields, but nothing on screen may call it saved.
        setOutcome({ workspaceId: asked, ok: false, text: res.error || 'That did not save.' })
        return
      }
      // ⚠️ Drop only the keys that were actually SENT.
      //
      // The fields are deliberately not disabled while a save is in flight — a merchant who
      // cannot type is a worse surface than one whose unsent edit survives — so state can be
      // written between the click and the response. Dropping the whole draft here discarded it:
      // type 50 into "a day", Save, type 20 into the per-customer field while the PUT is in
      // flight, and 20 reverted under a "SMS limits saved." banner. It is a lost edit reported
      // as a success.
      //
      // A key whose draft value still equals what was sent has nothing to say that the store
      // does not, so it goes and the field reads back the value `patchSmsConfig` mirrored from
      // the server — the displayed value is the STORED one, not a copy of what we hoped to
      // store. A key the merchant retyped is an unsaved edit and stays.
      setDraft((d) => {
        if (!d || d.workspaceId !== asked) return d
        const retyped = (Object.keys(sent) as (keyof Caps)[]).filter((k) => d.caps[k] !== sent[k])
        if (retyped.length === 0) return null
        const base = capsOf(useWorkspaceStore.getState().currentWorkspace?.smsConfig)
        return {
          workspaceId: asked,
          caps: retyped.reduce<FormCaps>((acc, k) => ({ ...acc, [k]: d.caps[k] }), base)
        }
      })
      setOutcome({ workspaceId: asked, ok: true, text: 'SMS limits saved.' })
    } catch (e) {
      setOutcome({
        workspaceId: asked,
        ok: false,
        text: e instanceof Error ? e.message : 'That did not save.'
      })
    } finally {
      setSaving((s) => (s === asked ? null : s))
    }
  }

  if (!workspaceId) return null

  // D-27: Save is live only while a save would change what is stored for THIS workspace — the
  // whole object `patchSmsConfig` would PUT, against the stored one (`storedValue.ts`). So a
  // workspace with no stored `smsConfig`, whose defaults these fields show, has something to
  // save, and retyping a cap back to its stored value leaves nothing to save. An empty field is
  // a change too; `handleSave` is what refuses to send it.
  const dirty = !matchesStored(mergeSmsConfig(currentWorkspace?.smsConfig, caps as Partial<SmsConfig>), currentWorkspace?.smsConfig)
  const barState = isSaving ? 'saving' : dirty ? 'dirty' : shown?.ok ? 'saved' : 'clean'
  const quietProblem = quietProblemOf(caps)

  return (
    <Card>
      <CardHeader
        titleAs="h2"
        title="Text messages"
        description="The most text messages Marva may send, and the hours she may not send them in."
      />

      <TileGrid>
        <CapField
          capKey="dailyLimitMessages"
          detail="Across your whole store."
          value={caps.dailyLimitMessages}
          onChange={(v) => set('dailyLimitMessages', v)}
        />
        <CapField
          capKey="monthlyLimitMessages"
          detail="Across your whole store."
          value={caps.monthlyLimitMessages}
          onChange={(v) => set('monthlyLimitMessages', v)}
        />
        <CapField
          capKey="maxSmsPerCustomerMonth"
          // ⚠️ NOT the TCPA. The TCPA governs consent and the calling window; it sets no
          // per-customer message count. Six a month is CTIA / carrier guidance, and citing the
          // wrong authority on a compliance screen is its own kind of wrong answer.
          detail="Six a month is what the CTIA and carrier guidance recommend as a maximum."
          value={caps.maxSmsPerCustomerMonth}
          onChange={(v) => set('maxSmsPerCustomerMonth', v)}
        />
      </TileGrid>

      {/* One rule, one evaluator (MAR-159). These two fields are the window every TEXT obeys,
          unioned with the 21:00–08:00 floor (`resolveQuietWindow('sms')` in
          `@marva/shared/quiet-window`, evaluated by `evaluateQuietHours` in `checkCanSendSms`).
          The floor itself, the immediate-reply exemption (within 15 minutes of the shopper's own
          text, `IMMEDIATE_REPLY_WINDOW_MS`) and "a held reply is not sent later" are stated once,
          on The fixed floor (MAR-385) — they are not this card's to change. Email's window is
          How often she writes; since MAR-154 this card does not touch an email decision. */}
      <Stack gap="sm">
        <FieldLabel as="h3">Quiet hours</FieldLabel>
        <FieldGrid>
          <Field
            as="select"
            label="Quiet from"
            value={caps.quietHoursStart}
            onChange={(e) => set('quietHoursStart', parseInt((e.target as HTMLSelectElement).value, 10))}
          >
            {HOURS.map((h) => (
              <option key={h} value={h}>
                {hourLabel(h)}
              </option>
            ))}
          </Field>
          <Field
            as="select"
            label="Quiet until"
            value={caps.quietHoursEnd}
            onChange={(e) => set('quietHoursEnd', parseInt((e.target as HTMLSelectElement).value, 10))}
          >
            {HOURS.map((h) => (
              <option key={h} value={h}>
                {hourLabel(h)}
              </option>
            ))}
          </Field>
        </FieldGrid>
        {/* MAR-163: said for the window being picked, not only on Save — the dropdowns offer
            every combination, so the control explains the ones that mean "never". */}
        {quietProblem && (
          <Alert tone="danger" live="assertive">
            {quietProblem.code === 'same_start_and_end'
              ? `Quiet from ${hourLabel(caps.quietHoursStart)} until ${hourLabel(caps.quietHoursEnd)} covers the whole day — Marva would never send another text.`
              : `With the 9:00 PM to 8:00 AM window that always applies, quiet from ${hourLabel(caps.quietHoursStart)} until ${hourLabel(caps.quietHoursEnd)} covers the whole day — Marva would never send another text.`}{' '}
            To stop text messages, switch SMS off in Channels instead.
          </Alert>
        )}
        <Hint>
          Marva starts no text between these hours, in the customer&apos;s local time where we know it, otherwise your
          store&apos;s — and never between 9pm and 8am, whatever you set here; see{' '}
          <Link to="/authority?tab=limits&section=floor">The fixed floor</Link>. 9:00 PM to 8:00 AM is the window US telemarketing rules set,
          and it is what a new store starts on. Email&apos;s quiet hours are in How often she writes.
        </Hint>
      </Stack>

      {shown && (
        <Alert tone={shown.ok ? 'success' : 'danger'} live={shown.ok ? undefined : 'assertive'}>
          {shown.text}
        </Alert>
      )}

      <SaveBar
        placement="footer"
        state={barState}
        note="Changes apply from her next message."
        saveLabel="Save SMS limits"
        onSave={handleSave}
      />
    </Card>
  )
}

export default SmsLimitsSection
